export const colors = {
  deepGreen: '#1B4030',
  frost: '#F8FCF9',
  charcoal: '#22211F',
  electricGreen: '#78C144',
  coolBlue: '#6CC6DE',

  background: '#F8FCF9',
  surface: '#FFFFFF',
  text: '#22211F',
  textMuted: '#6B6968',
  textInverse: '#F8FCF9',
  border: '#E5E7E5',
  // Progress / availability track. `track` is the empty channel on a light
  // surface; `trackMutedFill` is the desaturated fill for a depleted/closed
  // bar (full deal). On dark surfaces use `frostTint` for the channel.
  track: '#ECECE6',
  trackMutedFill: '#C7C7C0',
  primary: '#1B4030',
  primaryFg: '#F8FCF9',
  accent: '#78C144',
  accentText: '#3F7D21',
  accentFg: '#1B4030',
  info: '#6CC6DE',
  success: '#78C144',
  warning: '#E0A03B',
  // The filled part of a `warning` progress band (the upload band).
  warningFill: '#C4841F',
  danger: '#C13F3F',
  focusRing: 'rgba(120, 193, 68, 0.28)',

  // For use ON dark surfaces (Deep Green / charcoal). Hex+alpha forms of the
  // brand palette so we don't violate the "tokens only" rule when we need
  // translucency. Names describe role, not literal alpha.
  deepGreenOverlay: 'rgba(27, 64, 48, 0.72)', // bg-image dim on login
  glassFrostFill: 'rgba(248, 252, 249, 0.06)', // translucent card on dark
  glassFrostBorder: 'rgba(248, 252, 249, 0.10)',
  textOnDarkMuted: 'rgba(248, 252, 249, 0.7)', // subtitles on Deep Green
  textOnDarkSubtle: 'rgba(248, 252, 249, 0.6)', // tertiary nav links
  textOnDarkDisabled: 'rgba(248, 252, 249, 0.5)',
  pinCellOnDarkFill: 'rgba(248, 252, 249, 0.06)',
  pinCellOnDarkBorder: 'rgba(248, 252, 249, 0.25)',
  watermarkOnDark: 'rgba(248, 252, 249, 0.08)', // M/P watermark on dark cards
  textOnDarkStrong: 'rgba(247, 252, 251, 0.85)', // high-contrast text on dark
  dangerBright: '#FF8A85',
  accentTint: 'rgba(120, 193, 68, 0.18)',
  frostTint: 'rgba(248, 252, 249, 0.18)',
  brightGreenSoft: '#DEEED2',

  // Status-pill tones for the Refer tab's funnel rows and chips. These
  // shipped as literals duplicated between the pill constants and the chip
  // styles; promoted to tokens when WhoClickedRow split out of
  // WhoClickedCard and the duplication became cross-file.
  //
  // Deliberately NOT `warning` / `success` above: those are the alert
  // palette. These are quieter surface tints meant to sit inside a list row
  // without competing with the Electric Green earning moment.
  warnPillBg: '#FFF6E0',
  warnPillBorder: '#E8C76A',
  warnPillText: '#8B5A00',
  paidPillBg: '#E8F4DD',
  paidPillBorder: 'rgba(120, 193, 68, 0.4)',
  paidPillText: '#3B6D11',
  // Deal Steps v2: the step caption for "the user marked this done" and the
  // neutral tint for the tracker strip's empty state. The marked pill is
  // neutral on purpose (was a Cool Blue tint until launch night 2026-09-10):
  // a tinted check under orange "We're checking" pills read as MORE verified
  // than the steps the brand can vouch for. Only the brand's confirmation
  // gets a color.
  markedPillBg: '#F1F3F1',
  markedPillText: '#6B6968',
  neutralPillBg: '#F1F3F1',
  // Recessed page background under white cards (the deal page's content
  // sheet), so the cards read as raised instead of white-on-frost.
  surfaceSunken: '#F1F3F1',
  overduePillBg: '#F8E3E1',
  overduePillText: '#8B1E1E',
} as const;

export const fonts = {
  // Display = FK Screamer (matches production marketpryce.com h1).
  // Tall, narrow, condensed — the brand's primary headline voice.
  display: 'FKScreamer-Bold',
  displayBlack: 'FKScreamer-Black',
  displayMedium: 'FKScreamer-Medium',
  displayRegular: 'FKScreamer-Regular',

  // Numeric / wide sub-heads = Druk Wide. Used for big numbers
  // (MoneyDisplay), chunky stat values, and the M/P watermark — anywhere
  // the wide "scoreboard" feel reads better than FK Screamer's narrow.
  numeric: 'DrukWide-Bold',
  numericHeavy: 'DrukWide-Heavy',
  numericSuper: 'DrukWide-Super',
  subhead: 'DrukWide-Medium',

  // Body = Inter (matches production).
  body: 'Inter-Regular',
  bodyMedium: 'Inter-Medium',
  bodySemiBold: 'Inter-SemiBold',
  bodyBold: 'Inter-Bold',
} as const;

export const fontSizes = {
  // Tab bar labels only. Five tabs have to fit "Leaderboard" in a 75pt slot
  // on a 375pt screen; 11 truncates it to "Leaderbo…" there, 12 is worse.
  // 10 is also what React Navigation's uikit variant uses for the same reason.
  xxs: 10,
  xs: 12,
  sm: 14,
  base: 16,
  lg: 18,
  xl: 20,
  '2xl': 24,
  '3xl': 30,
  '4xl': 36,
  '5xl': 48,
  '6xl': 60,
  '7xl': 72,
  watermark: 200,
} as const;

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  base: 16,
  lg: 24,
  xl: 32,
  '2xl': 48,
  '3xl': 64,
} as const;

export const radii = {
  sm: 6,
  base: 10,
  lg: 20,
  xl: 24,
  // Hero-card corner. Reserved for the full-width cards that anchor a screen
  // (Home's balance and earnings heroes, the featured deal) — they sit on the
  // page background rather than inside it, and the extra 8pt over `lg` is what
  // separates "this is the screen" from "this is a row in it". Was a hardcoded
  // 28 in two files before it was a token; don't reach for it on anything that
  // isn't a screen-anchoring card.
  '2xl': 28,
  full: 9999,
} as const;

/**
 * Content width caps. In practice these only bite on desktop web: the iOS
 * build is portrait-only with `supportsTablet: false`, so native tops out
 * around 440pt — under the smaller token. They are NOT platform-gated
 * though, because a wide native viewport would want the same cap for the
 * same reason. Without one, a `flex: 1` row on a 2000px viewport stretches
 * its children to ~950px each, and any child with `aspectRatio: 1` becomes
 * a ~950px-tall image.
 *
 * `column` is one column of fields, copy, or a single image; `wide` is
 * anything with a multi-column grid, which needs the extra room to keep
 * tiles square-ish.
 */
export const contentWidths = {
  column: 480,
  wide: 760,
} as const;

export const shadows = {
  card: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 2,
  },
  modal: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.18,
    shadowRadius: 24,
    elevation: 8,
  },
  // Electric-green glow under primary CTAs. Brand-defining.
  // Mirrors handoff: box-shadow: 0 6px 14px rgba(119, 193, 68, 0.35).
  ctaGlow: {
    shadowColor: '#78C144',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.45,
    shadowRadius: 14,
    elevation: 6,
  },
} as const;

export const theme = {
  colors,
  fonts,
  fontSizes,
  spacing,
  radii,
  shadows,
  contentWidths,
} as const;

export type Theme = typeof theme;
export type Colors = keyof typeof colors;
export type Spacing = keyof typeof spacing;
export type Radii = keyof typeof radii;
export type ContentWidths = keyof typeof contentWidths;
export type FontSizes = keyof typeof fontSizes;
